import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getClasses, getSessions } from '@/api/teacher';
import { Select } from '@/components/kit/ui';
import { asList, unwrap } from '@/components/kit/data';

export interface MyClass { class_id: number; class: string; is_form_teacher: boolean; students: number; subjects: { id: number; name: string }[] }
export interface TermOpt { id: number; name: string; session: string; is_current: boolean; status?: string }

export const newKey = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export function useMyClasses() {
  return useQuery({ queryKey: ['teacher-classes'], queryFn: () => getClasses().then((r) => asList<MyClass>(unwrap(r))) });
}

export function useTermOptions() {
  return useQuery({
    queryKey: ['school-sessions'],
    queryFn: () =>
      getSessions().then((r) => {
        const out: TermOpt[] = [];
        for (const s of asList<any>(unwrap(r))) {
          for (const t of s.terms ?? []) out.push({ id: t.id, name: t.name, session: s.name, is_current: !!t.is_current, status: t.status });
        }
        return out;
      }),
  });
}

export function useCurrentTermDefault(terms: TermOpt[] | undefined, value: string, onPick: (v: string) => void) {
  useEffect(() => {
    if (!value && terms?.length) onPick(String((terms.find((t) => t.is_current) ?? terms[terms.length - 1]).id));
  }, [terms, value, onPick]);
}

export function ClassSelect({ value, onChange, label = 'Class', formOnly }: { value: string; onChange: (v: string) => void; label?: string; formOnly?: boolean }) {
  const q = useMyClasses();
  const list = (q.data ?? []).filter((c) => !formOnly || c.is_form_teacher);
  return (
    <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{q.isLoading ? 'Loading...' : 'Select class'}</option>
      {list.map((c) => <option key={c.class_id} value={c.class_id}>{c.class}</option>)}
    </Select>
  );
}

export function TermSelect({ value, onChange, label = 'Term' }: { value: string; onChange: (v: string) => void; label?: string }) {
  const q = useTermOptions();
  useCurrentTermDefault(q.data, value, onChange);
  return (
    <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{q.isLoading ? 'Loading...' : 'Select term'}</option>
      {(q.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.session} - {t.name}{t.is_current ? ' (current)' : ''}</option>)}
    </Select>
  );
}

/** Subject select limited to the subjects the teacher teaches in the chosen class (all of theirs when none chosen). */
export function SubjectSelect({ classId, value, onChange, label = 'Subject', any }: { classId?: string; value: string; onChange: (v: string) => void; label?: string; any?: boolean }) {
  const q = useMyClasses();
  const subs = new Map<number, string>();
  for (const c of q.data ?? []) if (!classId || String(c.class_id) === classId) for (const s of c.subjects) subs.set(s.id, s.name);
  return (
    <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{any ? 'All subjects' : 'Select subject'}</option>
      {[...subs].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
    </Select>
  );
}

export interface ExamRow { id: number; title: string; kind: string; status: string; total_marks?: number; duration_minutes?: number }

export function ExamSelect({ exams, value, onChange }: { exams: ExamRow[]; value: string; onChange: (v: string) => void }) {
  return (
    <Select label="CBT exam" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select exam</option>
      {exams.map((e) => <option key={e.id} value={e.id}>{e.title} ({e.status})</option>)}
    </Select>
  );
}
